import React from 'react';
import styled, { css } from 'styled-components';
import {
  BaseColor,
  colors,
  Icon,
  RawHtml,
  TableDemoStyle,
  TextVariant,
  Text as Typography,
} from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import extractModelId from 'shared/utils/extract-model-id';
import extractCQStyle, { CQStyleMap } from 'shared/utils/extractCQStyle';
import { TextProps } from './TextEditConfig';

const variantPropCQStyleMap: CQStyleMap<TextVariant> = {
  'text--bodyMDemi': 'bodyMDemi',
  'text--bodyMMedium': 'bodyMMedium',
  'text--bodySDemi': 'bodySDemi',
  'text--bodySMedium': 'bodySMedium',
  'text--footnoteDemi': 'footnoteDemi',
  'text--footnoteMedium': 'footnoteMedium',
  'text--codeSnippet': 'codeSnippet',
};

const colours: BaseColor[] = [
  'newZealandBlack',
  'alto',
  'wairua',
  'mawheranui',
  'greyLynn',
  'greymouth',
  'moeraki',
  'onepu',
];

const fontColourPropCQStyleMap: CQStyleMap<BaseColor> = {};

colours.forEach((color) => {
  fontColourPropCQStyleMap[`text-color--${color}`] = color;
});

export type LinkVariant = 'inverseMode' | 'inverseAlternateMode';

const linkPropCQStyleMap: CQStyleMap<LinkVariant> = {
  'linkStyle--inverseMode': 'inverseMode',
  'linkStyle--inverseAlternateMode': 'inverseAlternateMode',
};

export type BulletColorVariant = 'newZealandBlack' | 'mawhero';

const bulletColorPropCQStyleMap: CQStyleMap<BulletColorVariant> = {
  'listBulletColor--list1': 'mawhero',
  'listBulletColor--list2': 'newZealandBlack',
};

const tableDemoPropCQStyleMap: CQStyleMap<TableDemoStyle> = {
  'tableDemoStyle--lastColumn': 'lastColumn',
  'tableDemoStyle--allExceptFirstColumnAndRow': 'allExceptFirstColumnAndRow',
  'tableDemoStyle--secondRow': 'secondRow',
};

export type TableStyle = 'verticalDividers';

const tablePropCQStyleMap: CQStyleMap<TableStyle> = {
  'tableStyle--verticalDividers': 'verticalDividers',
};

export type InlineWarningStyleProp = 'inlineWarning';

const inlineWarningPropCQStyleMap: CQStyleMap<InlineWarningStyleProp> = {
  'text--inlineWarning': 'inlineWarning',
};

export type HorizontalScrollStyleProp = 'hasHorizontalScroll';

const hasHorizontalScrollPropCQStyleMap: CQStyleMap<HorizontalScrollStyleProp> =
  {
    'text--hasHorizontalScroll': 'hasHorizontalScroll',
  };

const alternateColumnColorsPropCQStyleMap: CQStyleMap<boolean> = {
  'tableStyle--alternateColumnColors': true,
};

const alternateRowColorsPropCQStyleMap: CQStyleMap<boolean> = {
  'tableStyle--alternateRowColors': true,
};

const largeCellPaddingPropCQStyleMap: CQStyleMap<boolean> = {
  'tableStyle--largeCellPadding': true,
};

export type ComponentVariantProp = 'additionalSpacing';

const componenStyleVariantPropCQStyleMap: CQStyleMap<ComponentVariantProp> = {
  'componentStyleVariant--additionalSpacing': 'additionalSpacing',
};

const CenteredGreyTableCell = css`
  ${({ theme: { palette } }) => css`
    background-color: ${palette.baseColors.onepu};
    text-align: center;
    vertical-align: center;
    padding: 0;
    border: 1px solid ${palette.baseColors.newZealandBlack};
  `}
`;

interface StyledRawHtmlProps {
  alternateRowColors: boolean;
  hasTableVerticalDividers: boolean;
}

export const StyledRawHtml = styled(RawHtml)<StyledRawHtmlProps>`
  ${({
    theme: { palette, borderRadius, spacing },
    tableDemoStyle,
    hasHorizontalScroll,
    alternateColumnColors,
    alternateRowColors,
    largeCellPadding,
    hasTableVerticalDividers,
  }) => css`
    overflow-x: ${hasHorizontalScroll ? 'scroll' : 'none'};

    table {
      border-spacing: 0;
      border-collapse: separate;
      width: 100%;w
      margin: 0;
      padding: 0;
      border-spacing: 0;
      box-sizing: border-box;
      border-radius: ${borderRadius.r1};
      border: 1px solid ${palette.tableBorder};

      ol {
        padding-left: ${spacing.s2};
        list-style-position: outside;
      }

      th {
        border: none;
        padding: ${spacing.s2};
        text-align: left;
        background: ${palette.tableHeaderCellBackground};
        box-sizing: border-box;
        color: ${palette.textColor};
        ${
          hasTableVerticalDividers &&
          css`
            border-right: 1px solid ${palette.tableBorder};
          `
        }

        ${
          alternateColumnColors &&
          css`
            &:nth-child(odd) {
              background: ${palette.tableHeaderCellBackground};
            }
          `
        }

        &:first-of-type {
          /* Needs to be 1px less than r1 */
          border-top-left-radius: 7px;
        }

        &:last-of-type {
          /* Needs to be 1px less than r1 */
          border-top-right-radius: 7px;

          border-right: none;
        }
      }

      tr {
        border: none;
        padding: 5px;
        margin-bottom: 10px;

        &:first-child td:first-of-type {
          border-top-left-radius: ${borderRadius.r1};
        }
        &:first-child td:last-of-type {
          border-top-right-radius: ${borderRadius.r1};
        }

        &:last-child td:first-of-type
        /* Needs to be 1px less than r1 */ {
          border-bottom-left-radius: 7px;
        }
        &:last-child td:last-of-type {
          /* Needs to be 1px less than r1 */
          border-bottom-right-radius: 7px;
        }

        &:last-of-type td {
          border: none;
          ${
            hasTableVerticalDividers &&
            css`
              border-right: 1px solid ${palette.tableBorder};
              &:last-of-type {
                border-right: none;
              }
            `
          }
        }

        ${
          alternateRowColors &&
          css`
            &:nth-child(odd) th,
            &:nth-child(odd) td {
              background: ${palette.tableHeaderCellBackground};
            }
          `
        }

        ${
          tableDemoStyle === 'secondRow' &&
          css`
            &:nth-child(2) td:not(td:first-of-type) {
              ${CenteredGreyTableCell};
            }
          `
        }
      }

      td {
        border: none;
        border-bottom: 1px solid ${palette.tableBorder};
        ${
          hasTableVerticalDividers &&
          css`
            border-right: 1px solid ${palette.tableBorder};
          `
        }

        padding: ${
          largeCellPadding
            ? `${spacing.s1} ${spacing.s2} ${spacing.s5} ${spacing.s2}`
            : spacing.s2
        };
        text-align: left;
        background: ${palette.tableDataCellBackground};
        color: ${palette.textColor};

        ${
          tableDemoStyle === 'allExceptFirstColumnAndRow' &&
          css`
            &:nth-child(n + 2) {
              ${CenteredGreyTableCell};
            }
          `
        }

        &:last-of-type {
          border-right: none;
        }

        ${
          tableDemoStyle === 'lastColumn' &&
          css`
            &:last-of-type {
              ${CenteredGreyTableCell};
            }
          `
        }

        ${
          alternateColumnColors &&
          css`
            &:nth-child(odd) {
              background: ${palette.tableHeaderCellBackground};
            }
          `
        }
      }
    }
  `}
`;

const InlineWarningWrapper = styled.div`
  ${({ theme: { spacing, palette } }) => css`
    padding: ${spacing.s3};
    background-color: ${palette.baseColors.greyLynn};

    svg {
      margin-bottom: 8px;
    }
  `}
`;

const Text = (props: React.PropsWithChildren<TextProps>) => {
  const {
    text,
    richText,
    appliedCssClassNames: cssClassNames,
    cqPath,
    regionId = 'text',
  } = props;

  const id = extractModelId(cqPath);

  const variantStyle =
    extractCQStyle(variantPropCQStyleMap, cssClassNames) || 'bodySMedium';

  const colourStyle = extractCQStyle(fontColourPropCQStyleMap, cssClassNames);
  const bulletColorStyle = extractCQStyle(
    bulletColorPropCQStyleMap,
    cssClassNames,
  );
  const linkStyle = extractCQStyle(linkPropCQStyleMap, cssClassNames) || '';
  const tableDemoStyle =
    extractCQStyle(tableDemoPropCQStyleMap, cssClassNames) || undefined;
  const inlineWarningStyle =
    extractCQStyle(inlineWarningPropCQStyleMap, cssClassNames) || '';
  const hasHorizontalScroll =
    extractCQStyle(hasHorizontalScrollPropCQStyleMap, cssClassNames) || '';
  const alternateColumnColors = Boolean(
    extractCQStyle(alternateColumnColorsPropCQStyleMap, cssClassNames),
  );
  const alternateRowColors = Boolean(
    extractCQStyle(alternateRowColorsPropCQStyleMap, cssClassNames),
  );
  const largeCellPadding = Boolean(
    extractCQStyle(largeCellPaddingPropCQStyleMap, cssClassNames),
  );
  const hasTableVerticalDividers = Boolean(
    extractCQStyle(tablePropCQStyleMap, cssClassNames),
  );

  const textColor = colourStyle ? colors[colourStyle] : undefined;
  const bulletColor = bulletColorStyle ? colors[bulletColorStyle] : undefined;

  const componentStyleVariant = extractCQStyle(
    componenStyleVariantPropCQStyleMap,
    cssClassNames,
  );

  if (inlineWarningStyle === 'inlineWarning') {
    return (
      <LinkRegion regionId={regionId}>
        <InlineWarningWrapper>
          <Icon name="warning_small" fill={textColor} />
          <StyledRawHtml
            id={id}
            html={text}
            variant={variantStyle}
            color={textColor}
            bulletColor={bulletColor}
            linkStyle={linkStyle}
            tableDemoStyle={tableDemoStyle}
            data-rte-editelement
          />
        </InlineWarningWrapper>
      </LinkRegion>
    );
  }

  if (richText) {
    return (
      <LinkRegion regionId={regionId}>
        <StyledRawHtml
          id={id}
          html={text}
          variant={variantStyle}
          color={textColor}
          bulletColor={bulletColor}
          linkStyle={linkStyle}
          alternateColumnColors={alternateColumnColors}
          alternateRowColors={alternateRowColors}
          largeCellPadding={largeCellPadding}
          tableDemoStyle={tableDemoStyle}
          hasHorizontalScroll={hasHorizontalScroll}
          hasTableVerticalDividers={hasTableVerticalDividers}
          componentStyleVariant={componentStyleVariant}
          data-rte-editelement
        />
      </LinkRegion>
    );
  }

  return (
    <LinkRegion regionId={regionId}>
      <Typography component="p" variant={variantStyle} color={textColor}>
        {text}
      </Typography>
    </LinkRegion>
  );
};

export default Text;
